import { useRef } from 'react';
import { ArrowUpTrayIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/useToast';
import { ATTACHMENT_ACCEPT, ATTACHMENT_ACCEPTED_TYPES, MAX_ATTACHMENTS, MAX_ATTACHMENT_BYTES } from '../../dpRequests.definitions';
import { newPendingFile } from '../../services/dpAttachmentUpload';
import type { AttachmentPickerProps } from './attachmentPicker.types';

export function AttachmentPicker({
    files,
    onAdd,
    onRemove,
    label = 'Anexos',
    hint,
    required = false,
    disabled = false,
    pdfOnly = false,
    kind,
    maxFiles = MAX_ATTACHMENTS,
}: AttachmentPickerProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();
    const canAddMore = files.length < maxFiles && !disabled;

    function handleFile(file: File) {
        const acceptedTypes = pdfOnly ? ['application/pdf'] : ATTACHMENT_ACCEPTED_TYPES;
        if (!acceptedTypes.includes(file.type)) {
            toast.error('Tipo de arquivo não aceito', pdfOnly ? 'Envie o arquivo em PDF.' : 'Envie PDF, JPG ou PNG.');
            return;
        }
        if (file.size >= MAX_ATTACHMENT_BYTES) {
            toast.error('Arquivo muito grande', 'O limite é 10 MB.');
            return;
        }
        onAdd(newPendingFile(file, kind));
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-raro-gray-800">
                    {label}
                    {required && <span className="ml-1 text-rose-600">*</span>}
                </p>
                <span className="text-xs text-raro-gray-400">{files.length}/{maxFiles}</span>
            </div>
            {hint && <p className="text-xs text-raro-gray-500">{hint}</p>}

            {files.length > 0 && (
                <ul className="space-y-2">
                    {files.map((item, index) => (
                        <li key={item.id} className="flex items-center gap-3 rounded-xl border border-raro-gray-200 bg-white px-3 py-2">
                            <DocumentIcon className="h-5 w-5 shrink-0 text-primary-500" aria-hidden="true" />
                            <span className="min-w-0 flex-1 truncate text-sm text-raro-gray-700">{item.file.name}</span>
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => onRemove(index)}
                                    className="rounded-md p-1 text-raro-gray-400 hover:bg-raro-gray-100 hover:text-raro-gray-600"
                                    aria-label={`Remover ${item.file.name}`}
                                >
                                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {canAddMore && (
                <>
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary-200 px-4 py-3 text-sm font-medium text-primary-700 transition hover:border-primary-400 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
                    >
                        <ArrowUpTrayIcon className="h-5 w-5" aria-hidden="true" />
                        Anexar arquivo
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={pdfOnly ? 'application/pdf' : ATTACHMENT_ACCEPT}
                        className="sr-only"
                        onChange={event => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (file) handleFile(file);
                        }}
                    />
                </>
            )}
        </div>
    );
}
